import axios from "axios";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { handleMutationError } from "./handleMutationError";

/**
 * Healthcare-specific React Query configuration
 *
 * @see README.md for detailed HIPAA & Healthcare compliance considerations
 */
const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      retry: 0, // don't auto retry
    },
    queries: {
      refetchOnReconnect: true, // But should refetch on network reconnect
      refetchOnWindowFocus: false, // Healthcare apps shouldn't auto-refetch on focus
      retry: (failureCount, error: unknown) => {
        // Healthcare-specific retry logic
        if (axios.isAxiosError(error)) {
          // Don't retry auth errors
          if (error.response?.status === 401) return false;

          // Don't retry client errors (4xx)
          if (error.response?.status >= 400 && error.response?.status < 500) return false;

          // Don't retry fatal server errors
          if (error.response?.status === 500) return false;

          // Retry network/timeout errors and recoverable server errors (502, 503, 504)
          if (
            error.code === "NETWORK_ERROR" ||
            error.code === "ECONNABORTED" ||
            [502, 503, 504].includes(error.response?.status || 0)
          ) {
            return failureCount < 3;
          }
        }

        return failureCount < 3;
      },
      staleTime: 0, // react-query's default
    },
  },
  mutationCache: new MutationCache({
    // Route all mutation errors through the global handler.
    // mutation.meta carries per-mutation context (component, patientId, etc.)
    // set via `useMutation({ meta: { ... } })` at call sites.
    onError: (error, _variables, _context, mutation) => handleMutationError(error, mutation.meta),
  }),
});

type QueryProviderProps = {
  children: React.ReactNode;
};

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
